/**
 * Detects "the lazy chunk I asked for is not on the server any more".
 *
 * This happens on every deploy: uploadProd rsyncs with --delete, so the hashed chunk files a
 * still-open browser tab knows about are replaced by new ones. The moment that user clicks
 * something lazy, the fetch 404s. Once parts of the app are behind lazy routes this is no longer
 * a rare event, and if it is not handled the click does NOTHING and the app looks broken.
 *
 * The message differs per bundler and per browser, so match them all:
 *   esbuild / native ESM, Chrome : "Failed to fetch dynamically imported module: <url>"
 *   esbuild / native ESM, Firefox: "error loading dynamically imported module"
 *   esbuild / native ESM, Safari : "Importing a module script failed"
 *   webpack (legacy builder)     : "Loading chunk 123 failed"
 */
const CHUNK_ERROR_PATTERNS: RegExp[] = [
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Loading chunk \S+ failed/i,
    /Loading CSS chunk \S+ failed/i,
];

export function isChunkLoadError(error: any): boolean {
    if (!error) {
        return false;
    }
    const text = typeof error === "string"
        ? error
        : `${error?.message || ""} ${error?.name || ""} ${error?.stack || ""}`;
    if (!text || !text.trim()) {
        return false;
    }
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(text));
}

/** one retry per tab, so a genuinely broken chunk can never put us in a reload loop */
const RETRY_FLAG = "ss_chunk_reload_attempted";

export function chunkReloadAlreadyTried(): boolean {
    try {
        return sessionStorage.getItem(RETRY_FLAG) === "1";
    } catch (e) {
        return false;
    }
}

export function markChunkReloadTried(): void {
    try {
        sessionStorage.setItem(RETRY_FLAG, "1");
    } catch (e) {
        // private mode / blocked storage: worst case we retry again, which is still better
        // than leaving the user on a dead click
    }
}

/**
 * Called once a navigation succeeds, which proves lazy chunks are loading again. Without this the
 * flag would survive for the life of the tab, and a SECOND deploy during a long session would be
 * back to a dead click because the one allowed retry had already been spent.
 */
export function clearChunkReloadFlag(): void {
    try {
        if (sessionStorage.getItem(RETRY_FLAG)) {
            sessionStorage.removeItem(RETRY_FLAG);
        }
    } catch (e) {
        // nothing to do - storage unavailable
    }
}

/**
 * Recovery for a lazy import that is NOT a route change - opening a lazily loaded dialog, say.
 *
 * Deliberately asks first instead of reloading on its own: unlike a navigation, the user did not
 * ask to leave the page, and reloading the editor out from under them would throw away unsaved
 * scene work. Returns true if a reload was started.
 */
export function promptChunkReload(error: any): boolean {
    if (!isChunkLoadError(error)) {
        return false;
    }
    if (confirm("Found new version of SignStudio, reload App?")) {
        markChunkReloadTried();
        window.location.reload();
        return true;
    }
    return false;
}
